import { useState, useEffect, useRef } from 'react';
import {
    useLocation,
    useNavigate,
} from 'react-router-dom';
import styled from 'styled-components';
import {
    Card,
    Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { usePDF } from 'react-to-pdf';

import { useLoadingScreen } from '../../Store/LoadingManager';

import { IconChevronLeft, IconEdit, IconDeviceFloppy, IconCancel, IconTrash, IconPrinter, IconSquareArrowRight, IconCoin } from '@tabler/icons-react';

import { useBillingHook } from '../../Hooks/Billings/billingHooks';
import { usePriceSettingsHook } from '../../Hooks/Maintenance/PriceSettings/priceSettingsHooks';

import { DetailsInvoice, BillingLicenseDetails, BillingPayment, BillingDetails, CheckoutData } from '../../Models/Billing/Billing';
import { PriceSetting, ModulePrice, UserTier, Storage } from '../../Models/Maintenance/PriceSettings/PriceSettings';

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';
import RequiredFieldHelper from '../../Utilities/Helpers/RequiredFieldHelper';
import RequiredParametersHelper from '../../Utilities/Helpers/RequiredParametersHelper';
import ComputationsHelper from '../../Utilities/Helpers/ComputationsHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { CurrencyEnum } from '../../Utilities/Enums/CurrencyEnums';
import { DetailsInvoiceField, BillingDetailsField, BillingAddressField, BillingLicenseDetailsField, CheckoutDataField, BuyLicenseField, LicenseDetailsBuyField, GenerateLicenseField, PriceSettingsField } from '../../Utilities/Enums/ParametersEnum';
import { BillingStatusEnum, BillingStatus } from '../../Utilities/Enums/StatusEnums';
import { ValidityTypeEnum } from '../../Utilities/Enums/ValidityTypeEnums';

// import { ErrorMessage } from '../../Constants/Messages';

import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomDetailsCard from '../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';
import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import RibbonComponent from '../../CustomViews/Elements/RibbonComponent/RibbonComponent';

import BillDetails from './BillDetails';
import ItemDetails from './ItemDetails';
import AmountDetails from './AmountDetails';
import Checkout from './Checkout/Checkout';
import PrintBill from './PrintBill';
import ConfirmPaymentDialog from './ConfirmPaymentDialog';

import CompanyDropdown from '../../CustomViews/Elements/AutoComplete/CompanyDropdown';
import FormTextField from '../../CustomViews/Elements/FormTextField';
import FormDatePicker from '../../CustomViews/Elements/FormDatePicker';

const DetailsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none;
    border-radius: 0px !important;
    display: grid;
    grid-template-columns: auto;
    gap: 30px;
    box-shadow: none !important;
    padding: 10px 0px;
`;

const InvoiceForm = styled(Card) <{ $darkMode?: boolean; }>`
    // background: transparent !important;
    border: 1px solid;
    border-radius: 4px !important;
    box-shadow: none !important;
    padding: 25px;
    width: 80%;
    justify-self: center;
    position: relative;
    overflow: unset !important;

    // display: grid;
    // grid-template-columns: auto;
    // gap: 30px;

    .invoiceTextHeader {
        font-size: 25px;
        font-weight: 600;
        text-transform: uppercase;
        place-self: center;
        margin: 0px 0px 50px 0px; 
    }
`;

export default function InvoiceDetails() {
    // Variables
    let navigate = useNavigate();
    let location = useLocation();
    let { enqueueSnackbar } = useSnackbar();
    let { loading, setLoading } = useLoadingScreen();
    let queryParams = new URLSearchParams(location.search);

    let billingid = queryParams.get('id') ?? "0";
    let view = parseInt(queryParams.get('view') ?? ViewType.PREVIEW.toString());
    let ic = Boolean(queryParams.get('isCheckout')) ?? false;
    let requiredFields = RequiredParametersHelper.billings();

    //Use PDF
    const { toPDF, targetRef } = usePDF({
        filename: 'Invoice.pdf',
        method: 'open' // Set method to 'open'
    });

    // Use States
    const [queryStr, setQueryStr] = useState({
        pageSize: 10,
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        id: billingid,
    });
    let [viewType, setviewType] = useState(view ?? ViewType.ADD);
    let [openDelete, setopenDelete] = useState(false);
    let [toDeleteItem, settoDeleteItem] = useState<any>(null);
    let [formData, setformData] = useState<DetailsInvoice>(new DetailsInvoice());
    let [previewData, setpreviewData] = useState<DetailsInvoice>(new DetailsInvoice());
    let [errorMessages, setErrorMessages]: any = useState({});
    let [isCheckout, setisCheckout]: any = useState<boolean>(false);
    let [priceSettings, setpriceSettings] = useState<PriceSetting>(new PriceSetting());
    let [initData, setinitData]: any = useState<boolean>(false);
    let [isSuccessPayment, setisSuccessPayment]: any = useState<boolean>(false);
    let [openConfirmPaymentModal, setopenConfirmPaymentModal]: any = useState<boolean>(false);

    const q_queryKey = [CachingEnum.BILLINGS, queryStr]
    const { getBillingById, editBillingById, settleBillingById, deleteBillingById } = useBillingHook(q_queryKey, queryStr, false);
    const { getPriceSettings } = usePriceSettingsHook([CachingEnum.PRICE_SETTINGS], GlobalFunctionHelper.getCurrencyCode(), false);

    //Variables
    let detailsProps = {
        billingid: billingid,
        viewType: viewType,
        setviewType: setviewType,
        formData: formData,
        setformData: setformData,
        previewData: previewData,
        setpreviewData: setpreviewData,
        errorMessages: errorMessages,
        setErrorMessages: setErrorMessages,
        printBillRef: targetRef,
        isPrint: false,
        computeBillAmounts: async (_formData: DetailsInvoice) => {
            let _computeData = await ComputationsHelper.billingComputeAmounts(_formData, priceSettings);

            if(setformData) {
                setformData(_computeData);
            }
        }
    }

    //Event Handlers
    const getBillingData = async (isFromCheckout: boolean = false) => {
        if(billingid != '0') {
            let getDetails = await getBillingById(billingid);
            let getAdminUserProfile: any = await getBillingById(billingid);

            let _formData = {...formData};
            let _previewData = {...formData};

            if(getDetails?.data) {
                let _billingDetails = {...new BillingDetails() , ...getDetails?.data?.[DetailsInvoiceField.BILLING_DETAILS]};
                let _licenseDetails = {
                    ...new BillingLicenseDetails() ,
                    ...getDetails?.data?.[DetailsInvoiceField.LICENSE_DETAILS],
                    [BillingLicenseDetailsField.LICENSE_TYPE]: (getDetails?.data?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.LICENSE_TYPE] > 0 ?
                        getDetails?.data?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.LICENSE_TYPE] : 1
                    ),
                };
                let _paymentDetails = {...new BillingPayment() , ...getDetails?.data?.[DetailsInvoiceField.PAYMENT]};

                _previewData = {
                    ...formData,
                    ...getDetails.data,
                    [DetailsInvoiceField.TAX_RATE]: (getDetails.data?.[DetailsInvoiceField.TAX_RATE] > 0 ? getDetails.data?.[DetailsInvoiceField.TAX_RATE] : 8),
                    [DetailsInvoiceField.PENDING_ID]: getDetails?.data?.[DetailsInvoiceField.PUBLIC_ID] ?? null,
                    [DetailsInvoiceField.IS_WITH_ANNUAL_DISCOUNT]: _licenseDetails?.[BillingLicenseDetailsField.VALIDITY_TYPE] == ValidityTypeEnum.YEAR,
                    [DetailsInvoiceField.STATUS]: getDetails?.data?.[DetailsInvoiceField.STATUS] != BillingStatusEnum.ALL ? getDetails?.data?.[DetailsInvoiceField.STATUS] : BillingStatusEnum.UNPAID,
                    [DetailsInvoiceField.BILLING_DETAILS]: _billingDetails,
                    [DetailsInvoiceField.LICENSE_DETAILS]: _licenseDetails,
                    [DetailsInvoiceField.PAYMENT]: _paymentDetails,
                    [DetailsInvoiceField.CURRENCY_CODE]: getDetails.data?.[DetailsInvoiceField.CURRENCY_CODE] != null && getDetails.data?.[DetailsInvoiceField.CURRENCY_CODE] != "" ? getDetails.data?.[DetailsInvoiceField.CURRENCY_CODE] : GlobalFunctionHelper.getCurrencyCode(),
                    [DetailsInvoiceField.ANNUAL_DISCOUNT_RATE]: getDetails?.data?.[DetailsInvoiceField.ANNUAL_DISCOUNT_RATE] > 0 ? getDetails?.data?.[DetailsInvoiceField.ANNUAL_DISCOUNT_RATE] : (priceSettings?.[PriceSettingsField.ANNUAL_BILLING_DISCOUNT] ?? 0),
                };

                _formData = {
                    ..._previewData,
                    [DetailsInvoiceField.LICENSE_DETAILS]: {
                        ..._licenseDetails,
                        [BillingLicenseDetailsField.MODULES]: _licenseDetails?.[BillingLicenseDetailsField.MODULES]?.map((item: any) => { return (item?.publicId ?? "") }) ?? [],
                    },
                }
            }

            setformData(_formData);
            setpreviewData(_previewData);
            setisCheckout((!isFromCheckout ? (ic ?? false) : false));

            if(_previewData?.[DetailsInvoiceField.STATUS] == BillingStatusEnum.PAID && viewType != ViewType.PREVIEW) {
                setviewType(ViewType.PREVIEW);
                navigate(`${RouteEnum.BILLING_DETAILS}?id=${billingid}&view=${ViewType.PREVIEW}`, { replace: true });
            }

            initializePriceSettings(_formData?.[DetailsInvoiceField.CURRENCY_CODE] ?? GlobalFunctionHelper.getCurrencyCode());
        }
    }

    const initializePriceSettings = async (currencyCode: CurrencyEnum) => {
        let getCurrencyPriceSettings = await getPriceSettings({ currencyCode: currencyCode });

        setpriceSettings(getCurrencyPriceSettings?.data);
        setinitData(false);
    }

    const handleSubmit = async () => {
        let _validateForm = RequiredFieldHelper.validateForm(requiredFields, formData, errorMessages, setErrorMessages, enqueueSnackbar);
        
        if (_validateForm?.hasError) { return true; }

        let _success: boolean = false;

        let _formData: any = {
            [BuyLicenseField.INVOICE]: { ...formData },
            [BuyLicenseField.LICENSE_DETAILS]: {
                ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                [GenerateLicenseField.COMPANY_PUBLIC_ID]: formData?.[DetailsInvoiceField.COMPANY_PUBLIC_ID] ?? null,
                [LicenseDetailsBuyField.BILLING_DETAILS]: {
                    [GenerateLicenseField.SUB_TOTAL]: formData?.[DetailsInvoiceField.SUB_TOTAL] ?? 0,
                    [GenerateLicenseField.TAX_RATE]: formData?.[DetailsInvoiceField.TAX_RATE] ?? 0,
                    [GenerateLicenseField.TAX_AMOUNT]: formData?.[DetailsInvoiceField.TAX_AMOUNT] ?? 0,
                    [GenerateLicenseField.TOTAL_AMOUNT]: formData?.[DetailsInvoiceField.TOTAL_AFTER_ADJUSTMENT] ?? 0,
                    [GenerateLicenseField.IS_WITH_ANNUAL_DISCOUNT]: formData?.[DetailsInvoiceField.IS_WITH_ANNUAL_DISCOUNT] ?? false,
                    [GenerateLicenseField.DISCOUNT_APPLIED]: formData?.[DetailsInvoiceField.IS_WITH_ANNUAL_DISCOUNT] ?? false,
                    [GenerateLicenseField.NOTES]: formData?.[DetailsInvoiceField.REMARKS] ?? '',
                },
            },
            [BuyLicenseField.TRANSACTION_KEY]: GlobalFunctionHelper.getUUID(),
        };
        
        if(billingid) {
            let editResponse = await editBillingById(billingid, JSON.stringify(_formData));

            _success = editResponse?.isSuccess ?? false;
        }

        if(_success) {
            navigate(`${RouteEnum.BILLING_DETAILS}?id=${billingid}&view=${ViewType.PREVIEW}`, { replace: true });

            setviewType(ViewType.PREVIEW);

            getBillingData();
        }
    }

    const handleCloseDeleteConfirmation = async () => {
        setopenDelete(false);
        settoDeleteItem(null);
    }

    const handleSubmitSettleBilling = async (remarks: any) => {
        let _success: boolean = false;

        if(billingid) {
            let editResponse = await settleBillingById(billingid, JSON.stringify({remarks: remarks}));

            _success = editResponse?.isSuccess ?? false;
        }

        if(_success) {
            navigate(`${RouteEnum.BILLING_DETAILS}?id=${billingid}&view=${ViewType.PREVIEW}`, { replace: true });

            setopenConfirmPaymentModal(false);
            setviewType(ViewType.PREVIEW);

            getBillingData();   
        }
    }

    useEffect(() => {
        if(!initData) {
            getBillingData();
        }
    }, [billingid, initData]);

    useEffect(() => {
        if(!isCheckout && isSuccessPayment) {
            setisSuccessPayment(false);

            getBillingData(true);
        }
    }, [isCheckout, isSuccessPayment]);
    
    //Rendering Functions
    const renderActionButtons = () => {
        return <>
            <CustomButton
                btnType={ButtonType.CANCEL}
                title='Back'
                startIcon={<IconChevronLeft />}
                onClick={(e: any) => {
                    GlobalFunctionHelper.goBack(navigate, location, RouteEnum.BILLINGS);
                }} />

            {viewType == ViewType.PREVIEW && previewData?.[DetailsInvoiceField.STATUS] == BillingStatusEnum.PAID && <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Print Invoice'
                startIcon={<IconPrinter />}
                onClick={(e: any) => {
                    setLoading(true);
                    
                    if (targetRef.current) {
                        toPDF();
                    }

                    setLoading(false);
                }} />}

            {viewType == ViewType.PREVIEW && AccessibilityHelper.hasAccess(AdminModules.BILLING_DELETE) && previewData?.[DetailsInvoiceField.STATUS] != BillingStatusEnum.PAID && <CustomButton
                btnType={ButtonType.DELETE}
                title='Delete'
                startIcon={<IconTrash />}
                onClick={(e: any) => {
                    if (setopenDelete) {
                        setopenDelete(true);
                    }

                    if(settoDeleteItem) {
                        settoDeleteItem(previewData);
                    }
                }} />}
            {viewType == ViewType.EDIT && <CustomButton
                btnType={ButtonType.CANCEL}
                title='Cancel'
                startIcon={<IconCancel />}
                onClick={(e: any) => {
                    navigate(`${RouteEnum.BILLING_DETAILS}?id=${billingid}&view=${ViewType.PREVIEW}`, { replace: true });

                    if(setviewType) {
                        setviewType(ViewType.PREVIEW);
                    }
                }} />}
            {viewType == ViewType.PREVIEW && AccessibilityHelper.hasAccess(AdminModules.BILLING_EDIT) && previewData?.[DetailsInvoiceField.STATUS] != BillingStatusEnum.PAID && <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Edit'
                startIcon={<IconEdit />}
                onClick={(e: any) => {
                    navigate(`${RouteEnum.BILLING_DETAILS}?id=${billingid}&view=${ViewType.EDIT}`, { replace: true });
                    
                    if(setviewType) {
                        setviewType(ViewType.EDIT);
                    }
                }} />}

            {viewType == ViewType.PREVIEW && AccessibilityHelper.hasAccess(AdminModules.BILLING_EDIT) && previewData?.[DetailsInvoiceField.STATUS] != BillingStatusEnum.PAID && <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Update Payment'
                startIcon={<IconCoin />}
                onClick={(e: any) => {
                    setopenConfirmPaymentModal(true);
                }} />}

            
            {viewType == ViewType.PREVIEW && AccessibilityHelper.hasAccess(AdminModules.BILLING_EDIT) && previewData?.[DetailsInvoiceField.STATUS] != BillingStatusEnum.PAID && <CustomButton
                btnType={ButtonType.SUCCESS}
                title='Checkout'
                startIcon={<IconSquareArrowRight />}
                onClick={(e: any) => {
                    setisCheckout(true);
                }} />}
            {(viewType == ViewType.EDIT || viewType == ViewType.ADD) && <CustomButton
                btnType={ButtonType.SUCCESS}
                title='Save'
                startIcon={<IconDeviceFloppy />}
                onClick={(e: any) => {
                    handleSubmit();
                }} />}
        </>
    }

    const renderInvoiceDetails = () => {
        return <BillDetails {...detailsProps} />
    }

    const renderItemDetails = () => {
        return <ItemDetails {...detailsProps} />
    }

    const renderAmountDetails = () => {
        return <AmountDetails {...detailsProps} />
    }

    const renderBillingDetails = () => {
        return <>
            <CustomDetailsCard
            title={RouteHelper.getRouteDescription(RouteEnum.BILLING_DETAILS)}
            actionButtons={renderActionButtons()}
            isInvoiceDetails={true} >
                <InvoiceForm sx={{borderColor: 'customBorder.main', backgroundColor: 'customBackground.main' }}>
                    <RibbonComponent
                        position="ribbon-top-left"
                        colorScheme={previewData?.[DetailsInvoiceField.STATUS] == BillingStatusEnum.PAID ? "ribbon-success" : "ribbon-danger"}
                        content = {BillingStatus[previewData?.[DetailsInvoiceField.STATUS] ?? BillingStatusEnum.UNPAID]?.title ?? ""} />

                    <Typography className="invoiceTextHeader" color="primaryText">Invoice</Typography>
                    
                    {renderInvoiceDetails()}
                    {renderItemDetails()}
                    {renderAmountDetails()}
                </InvoiceForm>
            </CustomDetailsCard>

            {openDelete && <CustomDeleteConfirmation
                openDialog={openDelete}
                handleClose={handleCloseDeleteConfirmation}
                ItemToDelete={toDeleteItem?.[DetailsInvoiceField.TRANS_NO] ?? null} // Optional to display name of the item to be deleted
                showRemarksField={true}
                deleteItem={async (remarks: any) => {

                    if(toDeleteItem?.[DetailsInvoiceField.PUBLIC_ID]) {
                        let deleteReponse: any = await deleteBillingById(toDeleteItem?.[DetailsInvoiceField.PUBLIC_ID], remarks);

                        if(deleteReponse?.isSuccess) {
                            handleCloseDeleteConfirmation();
                            
                            navigate(RouteEnum.BILLINGS, { replace: true });
                        }
                    }
                }} />
            }
        </>
    }

    const renderCheckout = () => {
        return <Checkout
            formData={{
                [BuyLicenseField.INVOICE]: { ...formData },
                [BuyLicenseField.LICENSE_DETAILS]: {
                    ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                    [GenerateLicenseField.COMPANY_PUBLIC_ID]: formData?.[DetailsInvoiceField.COMPANY_PUBLIC_ID] ?? null,
                    [LicenseDetailsBuyField.BILLING_DETAILS]: {
                        [GenerateLicenseField.SUB_TOTAL]: formData?.[DetailsInvoiceField.SUB_TOTAL] ?? 0,
                        [GenerateLicenseField.TAX_RATE]: formData?.[DetailsInvoiceField.TAX_RATE] ?? 0,
                        [GenerateLicenseField.TAX_AMOUNT]: formData?.[DetailsInvoiceField.TAX_AMOUNT] ?? 0,
                        [GenerateLicenseField.TOTAL_AMOUNT]: formData?.[DetailsInvoiceField.TOTAL_AFTER_ADJUSTMENT] ?? 0,
                        [GenerateLicenseField.IS_WITH_ANNUAL_DISCOUNT]: formData?.[DetailsInvoiceField.IS_WITH_ANNUAL_DISCOUNT] ?? false,
                        [GenerateLicenseField.DISCOUNT_APPLIED]: formData?.[DetailsInvoiceField.IS_WITH_ANNUAL_DISCOUNT] ?? false,
                        [GenerateLicenseField.NOTES]: formData?.[DetailsInvoiceField.REMARKS] ?? '',
                    },
                },
                [BuyLicenseField.TRANSACTION_KEY]: GlobalFunctionHelper.getUUID(),
            }}
            isFromBilling={true}
            checkoutData={{
                ...new CheckoutData(),
                [CheckoutDataField.VALIDITY]: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY] ?? 0,
                [CheckoutDataField.VALIDITY_TYPE]: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY_TYPE] ?? ValidityTypeEnum.MONTH,
                [CheckoutDataField.MODULES]: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.MODULES],
                [CheckoutDataField.USER_CAPACITY]: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.USER_CAPACITY],
                [CheckoutDataField.STORAGE_CAPACITY]: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.STORAGE_CAPACITY],

                [CheckoutDataField.LINE_TOTAL]: formData?.[DetailsInvoiceField.LINE_TOTAL],
                [CheckoutDataField.SUB_TOTAL]: formData?.[DetailsInvoiceField.SUB_TOTAL],
                [CheckoutDataField.TAX_RATE]: formData?.[DetailsInvoiceField.TAX_RATE],
                [CheckoutDataField.TAX_AMOUNT]: formData?.[DetailsInvoiceField.TAX_AMOUNT],
                [CheckoutDataField.TOTAL_BEFORE_ADJUSTMENT]: formData?.[DetailsInvoiceField.TOTAL_BEFORE_ADJUSTMENT],
                [CheckoutDataField.ROUND_ADJUSTMENT]: formData?.[DetailsInvoiceField.ROUND_ADJUSTMENT],
                [CheckoutDataField.TOTAL_AFTER_ADJUSTMENT]: formData?.[DetailsInvoiceField.TOTAL_AFTER_ADJUSTMENT],
                [CheckoutDataField.BILLING_DETAILS]:  formData?.[DetailsInvoiceField.BILLING_DETAILS],
                [CheckoutDataField.DISCOUNT_AMOUNT]: formData?.[DetailsInvoiceField.DISCOUNT_AMOUNT],
                [CheckoutDataField.CURRENCY_CODE]: formData?.[DetailsInvoiceField.CURRENCY_CODE],
            }}
            onClickBackBtn={() => { setisCheckout(false); }}
            isSuccessPayment={isSuccessPayment}
            setisSuccessPayment={setisSuccessPayment} />
    }

    const renderPrintBill = () => {
        return <PrintBill {...{...detailsProps, viewType: ViewType.PREVIEW, isPrint: true}} />
    }

    return <DetailsContainer>
        {!isCheckout && renderBillingDetails()}
        {isCheckout && renderCheckout()}

        {renderPrintBill()}

        {openConfirmPaymentModal && <ConfirmPaymentDialog
            openDialog={openConfirmPaymentModal}
            handleClose={() => { setopenConfirmPaymentModal(false); }}
            buttonAction={(remarks) => {
                handleSubmitSettleBilling(remarks);
            }}
            invoiceId={previewData?.[DetailsInvoiceField.TRANS_NO] ?? ""}
            showRemarksField />}
    </DetailsContainer>
}
